import { ArrowDown, ArrowUp, Columns3, Download, Lock, MoreHorizontal, Plus, Star } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { FormField } from '@/types/api/form'
import {
  RECORD_VIEW_TYPES,
  VIEW_TYPE_LABELS,
  type RecordView,
  type RecordViewConfig,
  type RecordViewType,
  type RecordViewVisibility,
} from '@/types/api/record-view'
import { cn } from '@/utils/cn'

import { CHOICE_FIELD_TYPES, DATE_FIELD_TYPES } from './viewUtils'

/** Id of the built-in view every group has: the plain list of all records. */
export const ALL_RECORDS = 'all'

interface ViewDraft {
  name: string
  type: RecordViewType
  visibility: RecordViewVisibility
  groupBy?: string
  dateField?: string
  endDateField?: string
}

function FieldSelect({ label, fields, value, onChange, optional }: {
  label: string
  fields: FormField[]
  value: string | undefined
  onChange: (key: string | undefined) => void
  optional?: boolean
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value ?? '__none__'} onValueChange={(v) => onChange(v === '__none__' ? undefined : v)}>
        <SelectTrigger><SelectValue placeholder="Choose a field" /></SelectTrigger>
        <SelectContent>
          {optional && <SelectItem value="__none__">None</SelectItem>}
          {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

/** Create or edit dialog: name, layout, who sees it, and the field each layout needs. */
function ViewDialog({ open, onOpenChange, initial, fields, canShare, onSubmit, saving }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: ViewDraft
  fields: FormField[]
  canShare: boolean
  onSubmit: (draft: ViewDraft) => void
  saving: boolean
}) {
  const [draft, setDraft] = useState(initial)
  const patch = (p: Partial<ViewDraft>) => setDraft((d) => ({ ...d, ...p }))
  const choiceFields = fields.filter((f) => CHOICE_FIELD_TYPES.has(f.type))
  const dateFields = fields.filter((f) => DATE_FIELD_TYPES.has(f.type))
  const missingField =
    (draft.type === 'BOARD' && !draft.groupBy) ||
    ((draft.type === 'CALENDAR' || draft.type === 'TIMELINE') && !draft.dateField)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initial.name ? 'Edit view' : 'New view'}</DialogTitle>
          <DialogDescription>A view saves the layout, filters, sort and columns you are looking at.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="view-name">Name</Label>
            <Input id="view-name" value={draft.name} maxLength={80} autoFocus onChange={(e) => patch({ name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Layout</Label>
            <Select value={draft.type} onValueChange={(v) => patch({ type: v as RecordViewType })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RECORD_VIEW_TYPES.map((t) => <SelectItem key={t} value={t}>{VIEW_TYPE_LABELS[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {draft.type === 'BOARD' && (
            <FieldSelect label="Group cards by" fields={choiceFields} value={draft.groupBy} onChange={(groupBy) => patch({ groupBy })} />
          )}
          {(draft.type === 'CALENDAR' || draft.type === 'TIMELINE') && (
            <FieldSelect label={draft.type === 'CALENDAR' ? 'Date field' : 'Start date field'} fields={dateFields} value={draft.dateField} onChange={(dateField) => patch({ dateField })} />
          )}
          {draft.type === 'TIMELINE' && (
            <FieldSelect label="End date field" fields={dateFields} value={draft.endDateField} onChange={(endDateField) => patch({ endDateField })} optional />
          )}
          {canShare && (
            <div className="space-y-1.5">
              <Label>Visible to</Label>
              <Select value={draft.visibility} onValueChange={(v) => patch({ visibility: v as RecordViewVisibility })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIVATE">Only me</SelectItem>
                  <SelectItem value="SHARED">Everyone who can open this group</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!draft.name.trim() || missingField || saving} onClick={() => onSubmit(draft)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Show/hide and reorder the columns of a Table view. */
function ColumnsPicker({ fields, columns, onChange }: { fields: FormField[]; columns: string[]; onChange: (columns: string[]) => void }) {
  // Empty means "every field"; the picker works on the explicit list.
  const current = columns.length ? columns : fields.map((f) => f.key)
  const ordered = [...current.filter((k) => fields.some((f) => f.key === k)), ...fields.map((f) => f.key).filter((k) => !current.includes(k))]
  const move = (key: string, delta: number) => {
    const list = current.filter((k) => fields.some((f) => f.key === k))
    const i = list.indexOf(key)
    const j = i + delta
    if (i < 0 || j < 0 || j >= list.length) return
    ;[list[i], list[j]] = [list[j], list[i]]
    onChange(list)
  }
  const toggle = (key: string, on: boolean) => {
    const list = current.filter((k) => fields.some((f) => f.key === k))
    onChange(on ? [...list, key] : list.filter((k) => k !== key))
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm"><Columns3 className="mr-2 size-4" />Columns</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-80 w-64 space-y-1 overflow-y-auto p-2">
        {ordered.map((key) => {
          const field = fields.find((f) => f.key === key)
          if (!field) return null
          const on = current.includes(key)
          return (
            <div key={key} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
              <Checkbox checked={on} onCheckedChange={(v) => toggle(key, v === true)} aria-label={`Show ${field.label}`} />
              <span className="min-w-0 flex-1 truncate">{field.label}</span>
              {on && (
                <>
                  <button type="button" aria-label={`Move ${field.label} up`} className="text-muted-foreground hover:text-foreground" onClick={() => move(key, -1)}><ArrowUp className="size-3.5" /></button>
                  <button type="button" aria-label={`Move ${field.label} down`} className="text-muted-foreground hover:text-foreground" onClick={() => move(key, 1)}><ArrowDown className="size-3.5" /></button>
                </>
              )}
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

export interface ViewBarProps {
  views: RecordView[]
  activeId: string
  defaultViewId: string | null
  currentUserId: string | null
  /** May create, change and delete shared views and set the group default (record_group:update). */
  canManageShared: boolean
  canExport: boolean
  /** The filters, sort or columns on screen differ from the saved view. */
  dirty: boolean
  /** Data fields offered for columns and as board/calendar axes. */
  fields: FormField[]
  columns: string[]
  onColumnsChange: (columns: string[]) => void
  activeType: RecordViewType
  saving: boolean
  onSelect: (id: string) => void
  onCreate: (name: string, type: RecordViewType, visibility: RecordViewVisibility, config: Partial<RecordViewConfig>) => Promise<void>
  onEdit: (view: RecordView, name: string, type: RecordViewType, visibility: RecordViewVisibility, config: Partial<RecordViewConfig>) => Promise<void>
  onSaveCurrent: () => Promise<void>
  onDelete: (view: RecordView) => Promise<void>
  onSetDefault: (id: string | null) => Promise<void>
  onExport: () => void
}

export function ViewBar(props: ViewBarProps) {
  const { views, activeId, defaultViewId, currentUserId, canManageShared, fields } = props
  const [dialog, setDialog] = useState<{ view: RecordView | null } | null>(null)
  const active = views.find((v) => v.id === activeId) ?? null
  const canEditActive = !!active && (active.visibility === 'SHARED' ? canManageShared : active.ownerUserId === currentUserId)

  const tab = (id: string, label: string, extra?: { priv?: boolean }) => (
    <button
      key={id}
      type="button"
      onClick={() => props.onSelect(id)}
      aria-current={id === activeId ? 'true' : undefined}
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors',
        id === activeId ? 'border-primary bg-primary/10 font-medium text-foreground' : 'border-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {label}
      {extra?.priv && <Lock aria-label="Private" className="size-3" />}
      {(id === (defaultViewId ?? ALL_RECORDS)) && <Star aria-label="Default view" className="size-3 fill-current" />}
    </button>
  )

  const initial: ViewDraft = dialog?.view
    ? { name: dialog.view.name, type: dialog.view.type, visibility: dialog.view.visibility, groupBy: dialog.view.config.groupBy, dateField: dialog.view.config.dateField, endDateField: dialog.view.config.endDateField }
    : { name: '', type: 'TABLE', visibility: 'PRIVATE' }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex min-w-0 flex-1 basis-full items-center gap-1 overflow-x-auto lg:basis-0" role="tablist" aria-label="Record views">
        {tab(ALL_RECORDS, 'All records')}
        {views.map((v) => tab(v.id, v.name, { priv: v.visibility === 'PRIVATE' }))}
        <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setDialog({ view: null })}>
          <Plus className="mr-1 size-4" />View
        </Button>
      </div>
      <div className="flex items-center gap-2">
        {props.dirty && canEditActive && (
          <Button size="sm" disabled={props.saving} onClick={() => void props.onSaveCurrent()}>Save view</Button>
        )}
        {props.activeType === 'TABLE' && <ColumnsPicker fields={fields} columns={props.columns} onChange={props.onColumnsChange} />}
        {props.canExport && (props.activeType === 'TABLE' || props.activeType === 'LIST') && (
          <Button variant="outline" size="sm" onClick={props.onExport}><Download className="mr-2 size-4" />Export CSV</Button>
        )}
        {(active || canManageShared) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="View options"><MoreHorizontal className="size-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {active && canEditActive && <DropdownMenuItem onSelect={() => setDialog({ view: active })}>Edit view…</DropdownMenuItem>}
              {canManageShared && (!active || active.visibility === 'SHARED') && defaultViewId !== (active?.id ?? null) && (
                <DropdownMenuItem onSelect={() => void props.onSetDefault(active?.id ?? null)}>Make default view</DropdownMenuItem>
              )}
              {active && canEditActive && <DropdownMenuSeparator />}
              {active && canEditActive && (
                <DropdownMenuItem className="text-destructive" onSelect={() => void props.onDelete(active)}>Delete view</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {dialog && (
        <ViewDialog
          key={dialog.view?.id ?? 'new'}
          open
          onOpenChange={(open) => { if (!open) setDialog(null) }}
          initial={initial}
          fields={fields}
          canShare={canManageShared}
          saving={props.saving}
          onSubmit={async (d) => {
            const config = { groupBy: d.groupBy, dateField: d.dateField, endDateField: d.endDateField }
            if (dialog.view) await props.onEdit(dialog.view, d.name.trim(), d.type, d.visibility, config)
            else await props.onCreate(d.name.trim(), d.type, d.visibility, config)
            setDialog(null)
          }}
        />
      )}
    </div>
  )
}
